<template>
  <b-modal
      :visible="visible"
      :title="song ? `Привязать: ${song.artist_name} — ${song.title}` : 'Привязать'"
      size="lg"
      hide-footer
      @change="$emit('change', $event)"
      @show="reset"
  >
    <b-overlay :show="isSaving" rounded="sm">
      <b-tabs v-model="tabIndex" content-class="pt-1">
        <b-tab title="Наша база">
          <b-form-group :label="isAlbum ? 'Артист' : 'Трек'">
            <b-input-group>
              <b-form-input v-model="query" placeholder="Поиск" @keyup.enter="search"/>
              <b-input-group-append>
                <b-button variant="primary" :disabled="isSearching" @click="search">Найти</b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>

          <b-list-group v-if="results.length">
            <b-list-group-item
                v-for="item in results"
                :key="item.id"
                button
                class="d-flex justify-content-between align-items-center"
                @click="save(item)"
            >
              <span>{{ item.title }}</span>
              <small class="text-muted">#{{ item.id }}</small>
            </b-list-group-item>
          </b-list-group>
          <div v-else-if="searched" class="text-muted">Ничего не найдено</div>
        </b-tab>
        <!-- Добавление из Spotify создаёт артиста/трек мимо модерации — только админу -->
        <b-tab v-if="!isAlbum && $can('manage', 'all')" title="Spotify" @click="initSpotify">
          <b-input-group class="mb-1">
            <b-form-input v-model="spotifyQuery" placeholder="Поиск в Spotify" @keyup.enter="searchSpotify"/>
            <b-input-group-append>
              <b-button variant="primary" :disabled="isSpotifySearching" @click="searchSpotify">Найти</b-button>
            </b-input-group-append>
          </b-input-group>

          <b-overlay :show="isSpotifySearching" rounded="sm">
            <b-list-group v-if="spotifyResults.length">
              <b-list-group-item
                  v-for="item in spotifyResults"
                  :key="item.id"
                  class="d-flex align-items-center"
              >
                <b-img
                    v-if="item.image"
                    :src="item.image"
                    width="36"
                    height="36"
                    rounded
                    class="mr-50"
                />
                <div class="flex-grow-1">
                  <div>{{ item.artist }} — {{ item.title }}</div>
                  <small class="text-muted">{{ item.album }} · {{ item.year }}</small>
                </div>
                <b-link
                    :href="`https://open.spotify.com/track/${item.id}`"
                    target="_blank"
                    class="mr-50"
                    title="Открыть в Spotify"
                >
                  <feather-icon icon="ExternalLinkIcon"/>
                </b-link>
                <b-button
                    variant="success"
                    size="sm"
                    title="Добавить трек в базу и привязать"
                    @click="addFromSpotify(item)"
                >
                  <feather-icon icon="PlusIcon"/> Добавить
                </b-button>
              </b-list-group-item>
            </b-list-group>
            <div v-else-if="spotifySearched" class="text-muted">Ничего не найдено</div>
          </b-overlay>
          <small class="text-muted d-block mt-50">
            Артист и трек создаются, если их ещё нет в базе; к треку сохраняется Spotify ID.
            Если к песне уже привязан артист — трек добавится ему.
          </small>
        </b-tab>
      </b-tabs>

      <div v-if="song && (song.track || song.artist)" class="mt-1 text-right">
        <b-button variant="outline-danger" size="sm" @click="unlink">Отвязать</b-button>
      </div>
    </b-overlay>
  </b-modal>
</template>

<script>
import {
  BModal, BOverlay, BFormGroup, BInputGroup, BInputGroupAppend, BFormInput, BButton, BListGroup, BListGroupItem,
  BTabs, BTab, BImg, BLink,
} from 'bootstrap-vue'
import { computed, ref } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import chartsApi from './chartsApi'

export default {
  components: {
    BModal, BOverlay, BFormGroup, BInputGroup, BInputGroupAppend, BFormInput, BButton, BListGroup, BListGroupItem,
    BTabs, BTab, BImg, BLink,
  },
  model: {
    prop: 'visible',
    event: 'change',
  },
  props: {
    visible: { type: Boolean, default: false },
    song: { type: Object, default: null },
  },
  setup(props, { emit }) {
    const toast = useToast()
    const query = ref('')
    const results = ref([])
    const searched = ref(false)
    const isSearching = ref(false)
    const isSaving = ref(false)

    const tabIndex = ref(0)
    const spotifyQuery = ref('')
    const spotifyResults = ref([])
    const spotifySearched = ref(false)
    const isSpotifySearching = ref(false)

    // Альбомы привязываются только к артисту — у нас нет таблицы альбомов
    const isAlbum = computed(() => props.song && props.song.kind === 'albums')

    const reset = () => {
      query.value = props.song ? (isAlbum.value ? props.song.artist_name : props.song.title) : ''
      results.value = []
      searched.value = false
      tabIndex.value = 0
      spotifyQuery.value = props.song ? `${props.song.artist_name} ${props.song.title}` : ''
      spotifyResults.value = []
      spotifySearched.value = false
    }

    const search = async () => {
      if (!query.value) return
      isSearching.value = true
      try {
        const res = isAlbum.value
          ? await chartsApi.searchArtists(query.value)
          : await chartsApi.searchTracks(query.value)
        results.value = res.data.data
        searched.value = true
      } catch (e) {
        toast({
          component: ToastificationContent,
          props: { title: 'Не удалось выполнить поиск', icon: 'AlertTriangleIcon', variant: 'danger' },
        })
      } finally {
        isSearching.value = false
      }
    }

    const searchSpotify = async () => {
      if (!spotifyQuery.value) return
      isSpotifySearching.value = true
      try {
        const res = await chartsApi.searchSpotify(spotifyQuery.value)
        spotifyResults.value = res.data.data
        spotifySearched.value = true
      } catch (e) {
        toast({
          component: ToastificationContent,
          props: { title: 'Не удалось выполнить поиск в Spotify', icon: 'AlertTriangleIcon', variant: 'danger' },
        })
      } finally {
        isSpotifySearching.value = false
      }
    }

    // При первом открытии вкладки сразу ищем по «артист название» из чарта
    const initSpotify = () => {
      if (!spotifySearched.value && !isSpotifySearching.value) searchSpotify()
    }

    const addFromSpotify = async item => {
      isSaving.value = true
      try {
        const res = await chartsApi.addSpotifyTrack(props.song.id, {
          spotify_id: item.id,
          title: item.title,
          artist: (item.artists && item.artists[0]) || item.artist,
          year: item.year,
        })
        emit('linked', res.data.data)
        emit('change', false)
        toast({
          component: ToastificationContent,
          props: { title: 'Трек добавлен и привязан', icon: 'CheckIcon', variant: 'success' },
        })
      } catch (e) {
        toast({
          component: ToastificationContent,
          props: { title: 'Не удалось добавить трек', icon: 'AlertTriangleIcon', variant: 'danger' },
        })
      } finally {
        isSaving.value = false
      }
    }

    const submit = async payload => {
      isSaving.value = true
      try {
        const res = await chartsApi.linkSong(props.song.id, payload)
        emit('linked', res.data.data)
        emit('change', false)
      } catch (e) {
        toast({
          component: ToastificationContent,
          props: { title: 'Не удалось сохранить привязку', icon: 'AlertTriangleIcon', variant: 'danger' },
        })
      } finally {
        isSaving.value = false
      }
    }

    const save = item => submit(isAlbum.value ? { artist_id: item.id } : { track_id: item.id })
    const unlink = () => submit({ track_id: null, artist_id: null })

    return {
      query, results, searched, isSearching, isSaving, isAlbum, reset, search, save, unlink,
      tabIndex, spotifyQuery, spotifyResults, spotifySearched, isSpotifySearching, searchSpotify, initSpotify, addFromSpotify,
    }
  },
}
</script>
